import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import {
  AssetState,
  ConfirmStashedBillingAddress,
  MSG_DELETE_CONNECTED_BILLING_ADDRESS_WARNING,
  StashBillingAddressDeletionForConfirmation,
  StashBillingAddressSelectionForConfirmation,
  SubscriptionBillingState
} from '@platform-frontend/checkout';
import { BillingAddress, DeleteBillingAddress } from '@platform-frontend/marketplace';
import { ClearNotifications, Navigate, ShowWarningNotification, tapNextOrError } from '@platform-frontend/util';
import { Observable, forkJoin, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { BillingIdWithCallback } from '../../commercial-common/billing-id-with-callback';
import { NullableIdWithCallback } from '../../commercial-common/nullable-id-with-callback';

@UntilDestroy()
@Component({
  selector: 'yat-billing-address-selection',
  templateUrl: './billing-address-selection.component.html',
  styleUrls: ['./billing-address-selection.component.scss']
})
export class BillingAddressSelectionComponent {
  constructor(private activatedRoute: ActivatedRoute, private store: Store) {}

  @Select(SubscriptionBillingState.billingAddresses)
  billingAddresses$: Observable<BillingAddress[]>;

  @Select(SubscriptionBillingState.selectedBillingAddress)
  selectedBillingAddress$: Observable<BillingAddress | null>;

  @Select(AssetState.contractId)
  managedContractId$: Observable<string | null>;

  saveSelectedBillingAddress({ id, completeCallback }: NullableIdWithCallback): void {
    this.store
      .dispatch(new StashBillingAddressSelectionForConfirmation(id ?? 'NONE'))
      .pipe(
        switchMap(() => this.handleStashedBillingAddress()),
        tapNextOrError(() => completeCallback()),
        untilDestroyed(this)
      )
      .subscribe();
  }

  prepareDeleteBillingAddress(id: number): void {
    forkJoin([this.hasMoreThanOneAddress(), this.isSelected(id)])
      .pipe(
        filter(([hasMoreThanOneAddress, isSelected]) => hasMoreThanOneAddress && isSelected),
        switchMap(() => this.store.dispatch(new ShowWarningNotification(MSG_DELETE_CONNECTED_BILLING_ADDRESS_WARNING))),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private hasMoreThanOneAddress(): Observable<boolean> {
    return this.billingAddresses$.pipe(
      take(1),
      map(billingAddresses => billingAddresses.length > 1)
    );
  }

  deleteBillingAddress({ id, completeCallback }: BillingIdWithCallback): void {
    this.deleteAndNavigateIfNeeded(id, this.isConnected(id) ? new StashBillingAddressDeletionForConfirmation() : new DeleteBillingAddress(id))
      .pipe(
        tapNextOrError(() => completeCallback()),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private isConnected(id: number): boolean {
    return this.store.selectSnapshot(SubscriptionBillingState.connectedBillingAddress)?.id === id;
  }

  private isSelected(id: number): Observable<boolean> {
    return this.selectedBillingAddress$.pipe(
      take(1),
      map(selectedBillingAddress => id === selectedBillingAddress?.id)
    );
  }

  private deleteAndNavigateIfNeeded(id: number, action: StashBillingAddressDeletionForConfirmation | DeleteBillingAddress): Observable<unknown> {
    return this.isSelected(id).pipe(
      switchMap(isSelected => this.store.dispatch(action).pipe(map(() => isSelected))),
      switchMap(isSelected => (isSelected ? this.handleStashedBillingAddress() : of(null)))
    );
  }

  private handleStashedBillingAddress(): Observable<unknown> {
    if (this.store.selectSnapshot(SubscriptionBillingState.stashRequiresConfirmation)) {
      return this.store.dispatch(new Navigate(['../update-confirmation'], { relativeTo: this.activatedRoute }));
    } else {
      return this.store.selectSnapshot(SubscriptionBillingState.stashRequiresStorage)
        ? this.store.dispatch(new ConfirmStashedBillingAddress())
        : this.store.dispatch(new ClearNotifications());
    }
  }
}
